import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Alert, App, Empty, Skeleton } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  DisconnectOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  HourglassOutlined,
  PlayCircleFilled,
  RedoOutlined,
  StarFilled,
  SwapOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { educationApi, type QuizAnswerInput, type QuizAttemptQuestionsDto, type QuizSubmitResult } from '../../lib/wikiApi';
import { learnApi, type LearnQuiz } from '../../lib/api/learn';
import { flushPendingAttempts, isNetworkError, listPendingAttempts, savePendingAttempt } from '../../lib/offline-attempts';
import { Button, Ring } from '../ds';
import { QuestionInput } from './QuestionInput';
import { QUESTION_KIND_LABEL, firstUnanswered, isAnswered, pluralRu, quizDots } from './learn-utils';
import '../../pages/Learn.css';

/**
 * Тест (HUB 2.0, PageLearn «Тест» + PageMobile): экран правил перед стартом →
 * ОДИН вопрос на экран с точками-навигатором, таймером и «Назад / Далее» →
 * результат (кольцо, статус, разбор). Все 8 типов вопросов — QuestionInput.
 * Попытка — прежняя (startQuiz → submitQuiz); черновик ответов и номер
 * вопроса держим в localStorage по id попытки (перезагрузка не сбрасывает),
 * без сети ответы уходят в офлайн-очередь (lib/offline-attempts).
 *
 * Маршрут: /course/:courseId/quiz/:quizId
 */
const DRAFT_KEY = (attemptId: string): string => `wiki-quiz-draft:${attemptId}`;

interface Draft {
  answers: Record<string, QuizAnswerInput>;
  cur: number;
}

function loadDraft(attemptId: string): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY(attemptId));
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}

function saveDraft(attemptId: string, d: Draft): void {
  try {
    localStorage.setItem(DRAFT_KEY(attemptId), JSON.stringify(d));
  } catch {
    /* приватный режим — без черновика */
  }
}

function dropDraft(attemptId: string): void {
  try {
    localStorage.removeItem(DRAFT_KEY(attemptId));
  } catch {
    /* ignore */
  }
}

export function QuizPage(): JSX.Element {
  const { quizId, courseId } = useParams<{ quizId: string; courseId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { message, modal } = App.useApp();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['wiki', 'education', 'quiz', quizId],
    queryFn: () => learnApi.getQuiz(quizId!),
    enabled: !!quizId,
    retry: false,
  });

  const [attempt, setAttempt] = useState<QuizAttemptQuestionsDto | null>(null);
  const [answers, setAnswers] = useState<Record<string, QuizAnswerInput>>({});
  const [cur, setCur] = useState(0);
  const [submitted, setSubmitted] = useState<QuizSubmitResult | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!attempt?.deadlineAt || submitted) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [attempt?.deadlineAt, submitted]);

  // Черновик попытки — после каждого ответа/перехода.
  useEffect(() => {
    if (attempt && !submitted) saveDraft(attempt.attemptId, { answers, cur });
  }, [attempt, answers, cur, submitted]);

  const start = useMutation({
    mutationFn: () => educationApi.startQuiz(quizId!),
    onSuccess: (a) => {
      const d = loadDraft(a.attemptId);
      setAttempt(a);
      setAnswers(d?.answers ?? {});
      setCur(d ? Math.min(Math.max(0, d.cur), Math.max(0, a.questions.length - 1)) : 0);
      setSubmitted(null);
      setNow(Date.now());
      window.scrollTo({ top: 0 });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const submitMutation = useMutation({
    mutationFn: () => educationApi.submitQuiz(quizId!, Object.values(answers), attempt?.attemptId),
    onSuccess: async (r) => {
      if (attempt) dropDraft(attempt.attemptId);
      setSubmitted(r);
      window.scrollTo({ top: 0 });
      await qc.invalidateQueries({ queryKey: ['wiki', 'education'] });
      await qc.invalidateQueries({ queryKey: ['wiki', 'lesson'] });
      await refetch();
    },
    onError: async (e: Error) => {
      // Фаза 2.7: без сети — ответы в IndexedDB, дошлём при появлении сети.
      if (isNetworkError(e) && quizId) {
        try {
          await savePendingAttempt({ quizId, attemptId: attempt?.attemptId, answers: Object.values(answers) });
          message.info('Нет сети — ответы сохранены на устройстве и отправятся автоматически', 6);
          navigate(`/course/${courseId}`);
          return;
        } catch {
          /* IndexedDB недоступен — покажем обычную ошибку */
        }
      }
      message.error(e.message);
    },
  });

  const deadlineMs = attempt?.deadlineAt ? new Date(attempt.deadlineAt).getTime() : null;
  const secondsLeft = deadlineMs ? Math.max(0, Math.floor((deadlineMs - now) / 1000)) : null;
  // Время вышло — отправляем то, что есть.
  useEffect(() => {
    if (attempt && !submitted && secondsLeft === 0 && !submitMutation.isPending) submitMutation.mutate();
  }, [secondsLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  const dots = useMemo(() => (attempt ? quizDots(attempt.questions, answers, cur) : []), [attempt, answers, cur]);
  const answeredCount = useMemo(() => (attempt ? attempt.questions.filter((q) => isAnswered(q, answers[q.id])).length : 0), [attempt, answers]);

  if (isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (error) return <Alert type="error" showIcon message="Не удалось загрузить тест" description={(error as Error).message} action={<Link to={`/course/${courseId}`}>К курсу</Link>} />;
  if (!data) return <Empty description="Тест не найден" />;

  if (submitted && attempt) {
    return (
      <QuizResult
        quiz={data}
        attempt={attempt}
        result={submitted}
        courseId={courseId!}
        onRetry={() => {
          setSubmitted(null);
          setAttempt(null);
          setAnswers({});
          start.mutate();
        }}
      />
    );
  }

  if (!attempt) return <QuizIntro quiz={data} courseId={courseId!} starting={start.isPending} onStart={() => start.mutate()} />;

  const total = attempt.questions.length;
  const q = attempt.questions[cur];
  const last = cur === total - 1;
  const doSubmit = (): void => {
    const miss = firstUnanswered(attempt.questions, answers);
    if (miss < 0) {
      submitMutation.mutate();
      return;
    }
    const left = total - answeredCount;
    modal.confirm({
      title: `Без ответа ${left} ${pluralRu(left, 'вопрос', 'вопроса', 'вопросов')}`,
      content: 'Пропущенные вопросы засчитаются как неверные. Вернуться к первому пропущенному или отправить как есть?',
      okText: 'Отправить как есть',
      cancelText: 'Вернуться',
      onOk: () => submitMutation.mutate(),
      onCancel: () => setCur(miss),
    });
  };
  const timeLow = secondsLeft != null && secondsLeft < 60;

  return (
    <div className="fk-learn-quiz">
      <div className="fk-learn-quiz-top">
        <Link to={`/course/${courseId}`} className="fk-learn-back"><ArrowLeftOutlined /> Выйти из теста</Link>
        <span style={{ fontWeight: 700, fontSize: 14, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'center' }}>{attempt.title}</span>
        {secondsLeft != null ? (
          <span className="fk-learn-timer" data-low={timeLow} title="Осталось времени">
            <FieldTimeOutlined /> {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
          </span>
        ) : <span />}
      </div>

      <div className="fk-learn-dots" role="tablist" aria-label="Вопросы">
        {dots.map((d, i) => (
          <button
            key={attempt.questions[i].id}
            type="button"
            className="fk-learn-dot"
            data-state={d}
            aria-label={`Вопрос ${i + 1}${d === 'answered' ? ', отвечен' : ''}`}
            aria-current={i === cur ? 'step' : undefined}
            onClick={() => setCur(i)}
          />
        ))}
      </div>

      <section className="fk-learn-card fk-learn-qcard">
        <div className="fk-learn-qmeta">
          <b>Вопрос {cur + 1} из {total}</b>
          <span>·</span>
          <span>{QUESTION_KIND_LABEL[q.kind] ?? q.kind}</span>
          <span>·</span>
          <span>{q.points} {pluralRu(q.points, 'балл', 'балла', 'баллов')}</span>
        </div>
        <div className="fk-learn-qtext">{q.text}</div>
        <QuestionInput
          key={q.id}
          q={q}
          attemptId={attempt.attemptId}
          answer={answers[q.id]}
          onChange={(a) => setAnswers((s) => ({ ...s, [q.id]: a }))}
        />
        <div className="fk-learn-quiz-nav">
          <Button variant="ghost" disabled={cur === 0} onClick={() => setCur((c) => Math.max(0, c - 1))}>Назад</Button>
          <span className="fk-learn-answered fk-learn-muted" style={{ fontSize: 13 }}>Отвечено {answeredCount} из {total}</span>
          {last ? (
            <Button variant="primary" loading={submitMutation.isPending} icon={<CheckOutlined />} onClick={doSubmit}>Отправить ответы</Button>
          ) : (
            <Button variant={isAnswered(q, answers[q.id]) ? 'indigo' : 'ghost'} onClick={() => setCur((c) => Math.min(total - 1, c + 1))}>
              {isAnswered(q, answers[q.id]) ? 'Далее' : 'Пропустить'}
            </Button>
          )}
        </div>
      </section>
      <div className="fk-learn-offline"><DisconnectOutlined /> Ответы сохраняются на устройстве — пропала сеть, отправятся сами</div>
    </div>
  );
}

/** Экран перед тестом: факты (проходной, время, попытки) и правила. */
function QuizIntro({ quiz, courseId, starting, onStart }: { quiz: LearnQuiz; courseId: string; starting: boolean; onStart: () => void }): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { message } = App.useApp();
  // Офлайн-очередь (фаза 2.7): ответы этого теста ждут сети — показать и дать дослать.
  const offline = useQuery({ queryKey: ['wiki', 'offline-attempts'], queryFn: () => listPendingAttempts(), staleTime: 5_000 });
  const waiting = (offline.data ?? []).filter((a) => a.quizId === quiz.id).length;
  const flush = useMutation({
    mutationFn: () => flushPendingAttempts(),
    onSuccess: async (n) => {
      await qc.invalidateQueries({ queryKey: ['wiki', 'offline-attempts'] });
      await qc.invalidateQueries({ queryKey: ['wiki', 'education'] });
      if (n > 0) message.success(`Отправлено попыток: ${n}`);
      else message.info('Сеть пока недоступна — попробуем позже');
    },
  });
  const exhausted = quiz.attemptsRemaining === 0 && !quiz.bestPassed;
  const attemptsText = quiz.policy.maxAttempts == null ? 'без ограничений' : `${quiz.attemptsRemaining ?? 0} из ${quiz.policy.maxAttempts}`;
  const cooldown = quiz.policy.cooldownMinutes;
  return (
    <div className="fk-learn-quiz">
      <Link to={`/course/${courseId}`} className="fk-learn-back"><ArrowLeftOutlined /> К курсу</Link>
      <div className="fk-learn-intro">
        <section className="fk-learn-card fk-learn-pad" style={{ padding: 24 }}>
          <div className="fk-learn-eyebrow" style={{ color: 'var(--fk-success)' }}>Тест</div>
          <h1 style={{ margin: '4px 0 6px', fontSize: 24, fontWeight: 800, letterSpacing: -0.3 }}>{quiz.title}</h1>
          <div className="fk-learn-sub" style={{ fontSize: 14 }}>
            {quiz.totalQuestions} {pluralRu(quiz.totalQuestions, 'вопрос', 'вопроса', 'вопросов')}, по одному на экран.
            {quiz.hasManualReview ? ' Открытые и фото-ответы проверяет руководитель — итог появится после проверки.' : ''}
          </div>
          <div className="fk-learn-facts">
            <div className="fk-learn-fact"><div className="fk-learn-eyebrow">Проходной балл</div><b>{quiz.passingScore} %</b></div>
            <div className="fk-learn-fact"><div className="fk-learn-eyebrow">Время</div><b>{quiz.policy.timeLimitMinutes ? `${quiz.policy.timeLimitMinutes} мин` : 'без лимита'}</b></div>
            <div className="fk-learn-fact"><div className="fk-learn-eyebrow">Попытки</div><b>{attemptsText}</b></div>
          </div>
          {quiz.bestScore !== null && (
            <div style={{ fontSize: 13.5, marginBottom: 12 }}>
              Лучший результат: <b>{quiz.bestScore}%</b> {quiz.bestPassed ? <span style={{ color: 'var(--fk-success)' }}>· сдан</span> : null}
            </div>
          )}
          {quiz.pendingReview && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Есть попытка на ручной проверке — результат появится после проверки." />}
          {waiting > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Ответы ждут отправки"
              description="Тест был пройден без сети — ответы сохранены на устройстве и отправятся, когда появится интернет."
              action={<Button variant="ghost" size="sm" loading={flush.isPending} onClick={() => flush.mutate()}>Отправить сейчас</Button>}
            />
          )}
          {exhausted ? (
            <Alert
              type="warning"
              showIcon
              message={quiz.nextAttemptAt ? 'Следующая попытка позже' : 'Попытки исчерпаны'}
              description={quiz.nextAttemptAt ? `Следующая попытка: ${new Date(quiz.nextAttemptAt).toLocaleString('ru-RU')}` : 'Обратитесь к руководителю, если нужна ещё попытка.'}
            />
          ) : (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="primary" size="lg" className="fk-learn-cta" icon={<PlayCircleFilled />} loading={starting} onClick={onStart}>
                {quiz.openAttempt ? 'Продолжить попытку' : quiz.bestPassed ? 'Пройти ещё раз' : 'Начать тест'}
              </Button>
              <Button variant="ghost" size="lg" className="fk-learn-cta" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(`/course/${courseId}`))}>
                Повторить материал
              </Button>
            </div>
          )}
        </section>
        <aside className="fk-learn-card fk-learn-pad">
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Правила</div>
          {(quiz.policy.shuffleQuestions || quiz.policy.shuffleOptions) && (
            <div className="fk-learn-rule"><SwapOutlined /><span>{quiz.policy.shuffleQuestions ? 'Вопросы' : 'Варианты'}{quiz.policy.shuffleQuestions && quiz.policy.shuffleOptions ? ' и варианты' : ''} перемешиваются</span></div>
          )}
          <div className="fk-learn-rule"><EyeOutlined /><span>{quiz.policy.showCorrectAnswers ? 'Правильные ответы и пояснения покажем после отправки' : 'Правильные ответы не показываем — только верно/неверно'}</span></div>
          {quiz.policy.timeLimitMinutes ? <div className="fk-learn-rule"><ClockCircleOutlined /><span>Таймер запустится после «Начать»; время вышло — ответы отправятся сами</span></div> : null}
          {cooldown > 0 && <div className="fk-learn-rule"><HourglassOutlined /><span>Повторная попытка — через {cooldown >= 60 && cooldown % 60 === 0 ? `${cooldown / 60} ч` : `${cooldown} мин`}</span></div>}
          {quiz.points && <div className="fk-learn-rule"><StarFilled /><span>+{quiz.points.pass} очков за сдачу, +{quiz.points.firstTry} — с первой попытки</span></div>}
          <div className="fk-learn-rule"><DisconnectOutlined /><span>Пропала сеть — ответы сохранятся на телефоне и отправятся сами</span></div>
        </aside>
      </div>
    </div>
  );
}

/** Результат: кольцо (предварительно, если есть ручная проверка), статус, разбор. */
function QuizResult({
  quiz,
  attempt,
  result,
  courseId,
  onRetry,
}: {
  quiz: LearnQuiz;
  attempt: QuizAttemptQuestionsDto;
  result: QuizSubmitResult;
  courseId: string;
  onRetry: () => void;
}): JSX.Element {
  const byId = new Map(result.results.map((r) => [r.questionId, r]));
  const canRetry = !result.passed && !result.pendingReview && (result.attemptsRemaining == null || result.attemptsRemaining > 0) && !result.nextAttemptAt;
  const tone = result.pendingReview ? 'var(--fk-info)' : result.passed ? 'var(--fk-success)' : 'var(--fk-error)';
  const status = result.pendingReview ? 'На проверке' : result.passed ? 'Тест сдан' : 'Не сдан';
  const title = result.pendingReview ? 'Ответы отправлены на проверку' : result.passed ? 'Отлично, тест сдан!' : 'Пока не получилось';
  const text = result.pendingReview
    ? `Автоматически проверено ${result.correctCount} из ${result.totalCount}. Открытые ответы проверит руководитель — итог появится в курсе.`
    : `Правильных ответов ${result.correctCount} из ${result.totalCount} · проходной балл ${quiz.passingScore}%.` +
      (result.passed ? '' : result.attemptsRemaining == null ? ' Можно попробовать ещё раз.' : ` Осталось попыток: ${result.attemptsRemaining}.`) +
      (result.nextAttemptAt ? ` Следующая попытка: ${new Date(result.nextAttemptAt).toLocaleString('ru-RU')}.` : '');
  return (
    <div className="fk-learn-quiz">
      <section className="fk-learn-card fk-learn-result">
        <div style={{ position: 'relative' }}>
          <Ring value={result.score} size={132} stroke={10} color={tone} label={null} />
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
            <div>
              <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.5, lineHeight: 1 }}>{result.score}%</div>
              {result.pendingReview && <div className="fk-learn-muted" style={{ fontSize: 11, marginTop: 2 }}>предварительно</div>}
            </div>
          </div>
        </div>
        <div>
          <div className="fk-learn-eyebrow" style={{ color: tone }}>{status}</div>
          <h1 style={{ margin: '4px 0 8px', fontSize: 26, fontWeight: 800, letterSpacing: -0.4 }}>{title}</h1>
          <div className="fk-learn-sub" style={{ fontSize: 14, lineHeight: 1.5 }}>{text}</div>
          {result.courseCompleted && <div style={{ marginTop: 8, color: 'var(--fk-success)', fontWeight: 700 }}>🎉 Курс пройден полностью!</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="primary" className="fk-learn-cta" to={`/course/${courseId}`}>К курсу</Button>
            {canRetry && <Button variant="ghost" className="fk-learn-cta" icon={<RedoOutlined />} onClick={onRetry}>Пройти ещё раз</Button>}
            {result.passed && quiz.points && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: 'var(--fk-orange)', marginLeft: 'auto' }}>
                <StarFilled /> +{quiz.points.pass} очков
              </span>
            )}
          </div>
        </div>
      </section>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {attempt.questions.map((qq, idx) => {
          const r = byId.get(qq.id);
          const ok = r?.correct === true;
          const bad = r?.correct === false;
          const bg = ok ? 'var(--fk-success-soft)' : bad ? 'var(--fk-error-soft)' : 'var(--fk-info-soft)';
          const fg = ok ? 'var(--fk-success)' : bad ? 'var(--fk-error)' : 'var(--fk-info)';
          return (
            <div key={qq.id} className="fk-learn-card fk-learn-res-row">
              <span className="fk-learn-res-ic" style={{ background: bg, color: fg }}>{ok ? <CheckOutlined /> : bad ? <CloseOutlined /> : <HourglassOutlined />}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{idx + 1}. {qq.text}</div>
                {r?.correctOptionIds && r.correctOptionIds.length > 0 && bad && (
                  <div style={{ fontSize: 13, marginTop: 4, color: 'var(--fk-text-2)' }}>
                    Правильно: {qq.options.filter((o) => r.correctOptionIds!.includes(o.id)).map((o) => o.text).join(', ')}
                  </div>
                )}
                {r?.explanation && <div style={{ fontSize: 13, marginTop: 4, color: 'var(--fk-text-2)' }}>{r.explanation}</div>}
                {r?.correct == null && <div style={{ fontSize: 12.5, marginTop: 4, color: 'var(--fk-text-muted)' }}>На проверке у руководителя</div>}
              </div>
              <span className="fk-learn-mono" style={{ fontSize: 12.5, fontWeight: 700, color: fg }}>{ok ? `+${qq.points}` : bad ? '0' : '…'}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
